import { Box, Button, DialogContent, MenuItem, Select, TextField, Snackbar, Alert } from "@mui/material";
import React, { useState, useCallback, useEffect } from "react";
import { BglDialog, BglDialogTitle, BglDialogActions } from "components/dialog/BglDialogStyles";
import { SURVEY_TEMPLATE_TYPES } from "constants/global";
import { SurveyTemplateAPI } from "apis/surveyTemplateAPI";
import { useGlobalConfig } from "context/GlobalContext";
import { useSelector, useDispatch } from "react-redux";
import { selectCMSSelectedPilotDocId } from "store/dataReducers/cmsPilotSlice";
import { createTemplateAction, updateTemplatesAction } from "store/dataReducers/surveyTemplateSlice";

interface SurveyNewTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (templateDocumentId: string) => void;
}

export const SurveyNewTemplateDialog: React.FC<SurveyNewTemplateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId);
  
  const [templateType, setTemplateType] = useState<(typeof SURVEY_TEMPLATE_TYPES)[number]>(SURVEY_TEMPLATE_TYPES[0]);
  const [templateLabel, setTemplateLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTemplateType(SURVEY_TEMPLATE_TYPES[0]);
      setTemplateLabel('');
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (!isCreating) {
      onClose();
      setTemplateLabel('');
    }
  }, [isCreating, onClose]);

  const handleCreateTemplate = useCallback(async () => {
    if (!selectedPilotDocId || !templateLabel.trim() || isCreating) return;

    setIsCreating(true);
    try {
      const resp = await SurveyTemplateAPI.createTemplate(rc, selectedPilotDocId, {
        templateType,
        label: templateLabel.trim(),
      });

      if (resp.error) {
        setSnackbar({
          open: true,
          message: resp.error.message || 'Failed to create template',
          severity: 'error',
        });
      } else if (resp.data) {
        // Add new template to Redux store
        dispatch(createTemplateAction({ template: resp.data }));
        
        // Notify parent of success with the new template's documentId
        onSuccess(resp.data.documentId);
        
        setSnackbar({
          open: true,
          message: 'Template created successfully',
          severity: 'success',
        });
        
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          handleClose();
        }, 500);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'An unexpected error occurred',
        severity: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  }, [selectedPilotDocId, templateType, templateLabel, rc, dispatch, isCreating, onSuccess, handleClose]);

  return (
    <>
      <BglDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <BglDialogTitle title="Create New Template" onClose={handleClose} />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value as (typeof SURVEY_TEMPLATE_TYPES)[number])}
              label="Template Type"
              fullWidth
              variant="outlined"
            >
              {SURVEY_TEMPLATE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            <TextField
              autoFocus
              id="templateName"
              type="text"
              fullWidth
              variant="outlined"
              value={templateLabel}
              onChange={(e) => setTemplateLabel(e.target.value)}
              placeholder="Enter template name"
            />
          </Box>
        </DialogContent>
        <BglDialogActions>
          <Button onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateTemplate} 
            variant="contained" 
            disabled={!templateLabel.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </BglDialogActions>
      </BglDialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
